import { useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import * as selector from "../../selectors/selectors";
import { Loader, ThemeProvider } from '@aws-amplify/ui-react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { CorrectIcon, FalconLogo, LockGrayIcon } from '../../icon';
import { SecureFileService } from '../../services/SecureFileService';
import { appNote, ToasterTexts } from '../shared/constants';
import * as docxPreview from "docx-preview";
import { UtilityService } from '../../services/UtilityService';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { HmacSignatureUtilities } from '../../services/hmac-encryption-decryption';
import setToast from '../../helpers/toaster.helper';
import { ToasterType } from '../shared/toaster/toaster.model';

const FaceVerification = () => {
    const [loading, setLoading] = useState(true)
    const [fileId, setFileId] = useState<string>("")
    const [isTimeout, setIsTimeout] = useState(false)
    const [fileName, setFileName] = useState<string>("")
    const [errorMessage, setErrorMessage] = useState(null)
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice)
    const [createLivenessApiData, setCreateLivenessApiData] = useState(null)
    const [isVerificationSuccessful, setIsVerificationSuccessful] = useState(false)
    const [uri, setUri] = useState<string>("")
    const [blob, setBlob] = useState("")

    const containerRef = useRef(null);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        const fileNameFromUrl = urlParams.get("fileName") || "";
        const cleanFileName = fileNameFromUrl.split('&')[0];
        const fileIdFromUrl = urlParams.get("id")
        setFileId(fileIdFromUrl || "")
        setFileName(cleanFileName)
    }, [])

    useEffect(() => {
        if (!!fileId) {
            fetchCreateLiveness();
        }
    }, [fileId]);

    const fetchCreateLiveness = async () => {
        try {
            setLoading(true)
            SecureFileService.getSessionId({ id: fileId }).then((res) => {
                if (HmacSignatureUtilities.verifySignature(res.data, res.headers["falkonsignature"])) {
                    setCreateLivenessApiData(res.data)
                }
                else {
                    setToast(ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER);
                }
                setLoading(false)
            }).catch((err) => {
                setLoading(false)
                setErrorMessage(err.response?.data?.error)
            })
        } catch (error) {
            setErrorMessage('Error creating liveness session')
            setLoading(false);
        }
    }

    const handleAnalysisComplete = async () => {
        const fileName = getFileName()
        const { sessionId } = createLivenessApiData
        try {
            SecureFileService.VerifySecureFaceFile({ sessionId, fileName: fileName, fileId }).then(async (res) => {
                const readOnley = res.headers["read-only"]
                // if (HmacSignatureUtilities.verifySignature(res.data, res.headers["falkonsignature"])) {
                if (readOnley === "true") {
                    if (UtilityService.getSecureFileMediaExtension(fileName).includes("vnd.openxmlformats-officedocument.wordprocessingml.document")) {
                        const url = URL.createObjectURL(new Blob([res.data], { type: UtilityService.getSecureFileMediaExtension(fileName) }))
                        setTimeout(() => {
                            const buffer = res.data;
                            if (containerRef.current) {
                                containerRef.current.innerHTML = "";
                                docxPreview.renderAsync(buffer, containerRef.current, undefined, {
                                    className: "docx-wrapper"
                                });
                            }
                        }, 1000)
                        setUri(url)
                    }
                    else {
                        const blob = new Blob([new Uint8Array(res.data)], { type: UtilityService.getSecureFileMediaExtension(fileName) });
                        const dataUrl = await blobToDataUrl(blob);
                        const url = URL.createObjectURL(blob);
                        setBlob(dataUrl)
                        setUri(url)
                    }
                    setIsVerificationSuccessful(true)
                }
                else {
                    const url = URL.createObjectURL(new Blob([res.data]))
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', fileName.replace(/%20/g, ' '))
                    link.click()
                    setIsVerificationSuccessful(true)
                }
                // }
            }).catch((err) => {
                const uint8Array = new Uint8Array(err.response.data)
                const decoder = new TextDecoder('utf-8')
                const responseString = JSON.parse(decoder.decode(uint8Array))
                if (!!responseString?.error) {
                    setErrorMessage(responseString?.error)
                }
            })
        } catch (error) {
        }
    }

    const getFileName = () => {
        return fileName.replace(/%20/g, " ")
    }

    useEffect(() => {
        const disableShortcuts = (e: KeyboardEvent) => {
            if (e.ctrlKey && ["p", "s", "u"].includes(e.key.toLowerCase())) {
                e.preventDefault();
            }
            if (e.key === "PrintScreen") {
                navigator.clipboard.writeText("");
                alert("Screenshots are not allowed!");
            }
        };
        const disableRightClick = (e: MouseEvent) => e.preventDefault();

        document.addEventListener("contextmenu", disableRightClick);
        document.addEventListener("keydown", disableShortcuts);
        return () => {
            document.removeEventListener("contextmenu", disableRightClick);
            document.removeEventListener("keydown", disableShortcuts);
        };
    }, []);

    const blobToDataUrl = async (blob: Blob): Promise<string> => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
        });
    };

    return (
        <>
            {uri ?
                <div className={`w-p-100 ${IsMobileDevice ? "h-p-90" : "h-v-100"}`}>
                    <div className={`h-p-90 w-p-90 d-flex justify-center align-center p-relative mx-auto mt-2 ${UtilityService.getSecureFileMediaExtension(fileName).includes("vnd.openxmlformats-officedocument.wordprocessingml.document") ? "" : "b-around-text-field-border-4 br-2"}`} style={{ zIndex: 9999 }}>
                        {
                            UtilityService.getSecureFileMediaExtension(fileName).includes("image") ?
                                <img src={uri} style={{ backgroundPosition: "center", backgroundRepeat: "no-repeat", backgroundSize: "cover" }} className='display-media' />
                                : UtilityService.getSecureFileMediaExtension(fileName).includes("audio") ?
                                    <audio src={uri} controls controlsList="nodownload noplaybackrate" ></audio>
                                    : UtilityService.getSecureFileMediaExtension(fileName).includes("video") ?
                                        <video src={uri} style={{ width: '100%', height: 'auto', objectFit: 'contain' }} controls controlsList="nodownload noplaybackrate" />
                                        : UtilityService.getSecureFileMediaExtension(fileName).includes("vnd.openxmlformats-officedocument.wordprocessingml.document") ? <div ref={containerRef} className={`${IsMobileDevice ? "docx-container" : ""} h-p-100 o-y-scroll`}></div>
                                            : UtilityService.getSecureFileMediaExtension(fileName).includes("application/pdf") ?
                                                <DocViewer
                                                    documents={[{ uri: blob, fileType: "pdf", fileName: fileName }]}
                                                    pluginRenderers={DocViewerRenderers}
                                                    config={{
                                                        header: {
                                                            disableHeader: true,
                                                        },
                                                        pdfVerticalScrollByDefault: true,
                                                    }}
                                                    style={{ height: '100%', overflow: 'auto', width: "100%" }}
                                                />
                                                : <iframe src={`${uri}#toolbar=0`} className='w-p-100 h-v-98 o-scroll '></iframe>
                        }
                    </div>
                    <div className='h-p-7 d-flex justify-center align-center'>
                        <LockGrayIcon />
                        <span className='h4-regular mx-1 color-primary-inactive'>File Secured by</span>
                        <FalconLogo className='w-20' /> </div>
                </div>
                : <ThemeProvider>
                    {loading ? (
                        <div className="d-flex flex-direction justify-center align-center">
                            <Loader />
                        </div>)
                        :
                        (!IsMobileDevice ?
                            (
                                <div className={`d-flex flex-direction justify-center align-center p-relative h-v-100`}>
                                    <header className="d-flex flex-direction justify-center align-center">
                                        <div>
                                            <FalconLogo />
                                        </div>
                                        {!isVerificationSuccessful &&
                                            <div className='d-flex flex-direction justify-center align-center'>
                                                <div className="h2-semibold color-settings-heading">Access Secure File</div>
                                                <div className='h3-semibold color-settings-heading'>[{getFileName()}]</div>
                                                <div className='h5-semibold color-settings-heading mt-2'>Please verify your identity using face recognition</div>
                                            </div>
                                        }
                                    </header>
                                    <div style={{ width: '100%', maxWidth: '400px', aspectRatio: '1/1' }}>
                                        {!isVerificationSuccessful ? (
                                            isTimeout ? (
                                                <div className="h4-regular mt-20 justify-center align-center d-flex login-background p-3">
                                                    Timeout: Please try again later or refresh again.
                                                </div>
                                            ) : errorMessage ? (
                                                <div className="h3-semibold mt-20 justify-center align-center d-flex login-background p-3">
                                                    {errorMessage}
                                                </div>
                                            ) : (
                                                <FaceLivenessDetector
                                                    sessionId={createLivenessApiData?.sessionId}
                                                    region="us-east-1"
                                                    onAnalysisComplete={handleAnalysisComplete}
                                                    onError={(error) => {
                                                        if (error?.state === "CONNECTION_TIMEOUT") {
                                                            setIsTimeout(true)
                                                        } else {
                                                            setErrorMessage('Face Liveness Detection Error')
                                                        }
                                                    }}
                                                    components={{
                                                        PhotosensitiveWarning: () => null,
                                                    }}
                                                    displayText={{
                                                        hintCenterFaceInstructionText: "",
                                                        hintCenterFaceText: "",
                                                    }}
                                                />
                                            )
                                        ) : (
                                            <>
                                                <div className='m-auto login-background mt-2 br-2 pt-2 pb-4'>
                                                    <div className='mt-4'>
                                                        <div className="d-flex justify-center">
                                                            <CorrectIcon className={`w-6 h-6`} />
                                                        </div>
                                                        <div className={"mb-1 d-flex justify-center mt-3"}>
                                                            <span className={`h2-medium color-display-content t-align-center `}>
                                                                {appNote.FILE_DOWNLOAD_SUCCESS}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className='d-flex justify-center align-center mb-1'>
                                                        <LockGrayIcon />
                                                        <span className={`h5-regular ml-1`}>{appNote.FILE_SECURED_BY_FALKONSMS}</span>
                                                    </div>
                                                </div>
                                                <div className='d-flex flex-direction justify-center align-center'>
                                                    <div className="d-flex mt-4 justify-center align-center h1-semibold color-settings-heading">
                                                        Identity verified
                                                    </div>
                                                    <div className='h5-regular color-settings-heading mt-1'>You have been successfully authenticated</div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) :
                            (
                                <div className={`d-flex flex-direction align-center h-v-100`}>
                                    <header className="d-flex flex-direction justify-center align-center">
                                        <div className="mt-4">
                                            <FalconLogo />
                                        </div>
                                        {!isVerificationSuccessful &&
                                            <div className='d-flex flex-direction align-center'>
                                                <div className='h4-semibold color-settings-heading t-align-center'>[{getFileName()}]</div>
                                                <span className='h5-semibold color-settings-heading mt-2 t-align-center'>Please verify your identity using face recognition</span>
                                            </div>
                                        }
                                    </header>
                                    <div style={{ width: '64%', maxWidth: '400px', aspectRatio: '1/1' }}>
                                        {!isVerificationSuccessful ? (
                                            isTimeout ? (
                                                <div className="h4-semibold mt-20 t-align-center">
                                                    Timeout: Please try again later!
                                                </div>
                                            ) : errorMessage ? (
                                                <div className="h4-semibold mt-20 t-align-center">
                                                    {errorMessage}
                                                </div>
                                            ) : (
                                                <FaceLivenessDetector
                                                    sessionId={createLivenessApiData?.sessionId}
                                                    region="us-east-1"
                                                    onAnalysisComplete={handleAnalysisComplete}
                                                    onError={(error) => {
                                                        if (error?.state === "CONNECTION_TIMEOUT") {
                                                            setIsTimeout(true)
                                                        } else {
                                                            setErrorMessage('Face Liveness Detection Error')
                                                        }
                                                    }}
                                                    components={{
                                                        PhotosensitiveWarning: () => null,
                                                    }}
                                                    displayText={{
                                                        hintCenterFaceInstructionText: "",
                                                        hintCenterFaceText: "",
                                                    }}
                                                />
                                            )
                                        )
                                            : (
                                                <>
                                                    <div className='m-auto login-background mt-2 br-2 pt-2 pb-4'>
                                                        <div className='mt-4'>
                                                            <div className="d-flex justify-center">
                                                                <CorrectIcon className={`w-6 h-6`} />
                                                            </div>
                                                            <div className={"mb-1 d-flex justify-center mt-3"}>
                                                                <span className={`h2-medium color-display-content t-align-center `}>
                                                                    {appNote.FILE_DOWNLOAD_SUCCESS}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className='d-flex justify-center align-center my-1'>
                                                            <LockGrayIcon />
                                                            <span className={`h5-regular ml-1`}>{appNote.FILE_SECURED_BY_FALKONSMS}</span>
                                                        </div>
                                                    </div>
                                                    <div className='d-flex flex-direction justify-center align-center'>
                                                        <div className="d-flex mt-4 justify-center align-center h2-semibold color-settings-heading">
                                                            Identity Verified
                                                        </div>
                                                        <div className='h4-regular color-settings-heading mt-3 d-flex justify-center align-center'>You have been</div>
                                                        <div className='h4-regular color-settings-heading d-flex justify-center align-center'>successfully authenticated</div>
                                                    </div>
                                                </>
                                            )}
                                    </div>
                                </div>
                            )
                        )}
                </ThemeProvider>}
        </>
    )
}
export default FaceVerification